import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { FAME_SIGNET_BALKEN } from '../../ui/FameSignet';
import { begruessung, berlinerStunde } from '../../uebersicht/DashboardKopf';
import './splash.css';
import {
  AUSBLENDEN,
  balkenTakt,
  BUCHSTABEN_VERSATZ_MS,
  GESAMTDAUER_MS,
  KURVEN,
  PUNKTE,
  RASTER_BREITE_PX,
  RASTER_HOEHE_PX,
  STANDBILD_GESAMTDAUER_MS,
  STANDBILD_MS,
  TAKTE,
  WORTMARKE,
  WORTMARKE_TRENNUNG,
  type Takt,
} from './szenen';

/** Was eine Bewegung an ein Element hängt: die Klassen und ihre Zeiten. */
interface Bewegungsangabe {
  readonly className: string;
  readonly style?: CSSProperties;
}

/** Verzögerung, Dauer und Kurve einer Bewegung als Stilangabe. */
function taktStil(takt: Takt, kurve: string): CSSProperties {
  return {
    animationDelay: `${String(takt.versatzMs)}ms`,
    animationDuration: `${String(takt.dauerMs)}ms`,
    animationTimingFunction: kurve,
  };
}

/**
 * Das Startbild nach der Anmeldung — die Choreografie aus
 * `claude-design-concept/splash-screen/` in CSS, ohne deren Laufzeit.
 *
 * ===========================================================================
 * WAS ES IST UND WAS ES AUSDRÜCKLICH NICHT IST
 * ===========================================================================
 *
 * Es ist eine **Überlagerung**, kein Ladezustand. Die Anwendung rendert
 * darunter vollständig und lädt ihre Daten währenddessen; dieses Bild wartet
 * auf nichts und hält nichts auf. Wäre es ein Ladezustand — «erst wenn die
 * Startseite Zahlen hat, verschwindet es» —, machte es aus einer Anzeige eine
 * Verzögerung, und der erste Eindruck des Systems wäre, dass es langsam ist.
 * Deshalb hängt sein Ende ausschliesslich an der Zeitachse in `szenen.ts` und
 * an nichts sonst.
 *
 * **Ein Klick oder eine Taste beendet es sofort**, ohne Nachlauf: Wer es
 * wegklickt, will die Anwendung sehen und nicht noch 400 ms Blende. Die
 * Lauscher hängen am Fenster und nicht nur an dieser Fläche — eine Taste
 * trifft kein `<div>`.
 *
 * ===========================================================================
 * KEINE ERFUNDENE ZAHL, NIRGENDS
 * ===========================================================================
 *
 * Die Vorlage zeigt unter der Wortmarke eine Fassungszeile und unter der
 * Begrüssung den Satz «12 Belege warten auf Zuordnung». Beides sind dort
 * **Platzhalter**, und beides steht hier nur, wenn der Aufrufer es mitgibt:
 * `version` und `hinweis` sind optional, und was fehlt, entfällt. Eine
 * ausgedachte Belegzahl auf dem ersten Bild nach der Anmeldung wäre die
 * schlechteste Zahl, die dieses System je zeigt — sie sähe aus wie eine
 * Auskunft und wäre eine Dekoration.
 *
 * **Die Belegzahl bleibt bis heute aus, und das ist kein Versehen.** Sie holt
 * die Startseite selbst; die Zahl liegt zum Zeitpunkt dieses Bildes noch
 * nicht vor, und ein eigener Abruf dafür widerspräche dem Absatz darüber —
 * die Aufrufstelle dafür ist da, die Zahl ist es nicht.
 *
 * **Die Fassung dagegen gibt der Aufrufer seit `W-versionbundle` mit.** Bis
 * dahin stand sie ausschliesslich in `apps/api/src/version.generated.ts` —
 * einer zur Bauzeit der API erzeugten Datei, die das Browserbündel nicht
 * kannte; die einzige Quelle im Browser war `GET /api/v1/system/status`, was
 * eine Netzrunde und die Fähigkeit `system:read` kostet, also für das erste
 * Bild nach der Anmeldung zu spät kam. `apps/web/vite.version.ts` liest
 * seither dieselbe Ableitung wie die API (`messeFassung()` aus
 * `scripts/generate-version.mjs`, wiederverwendet statt zweimal geschrieben)
 * und `apps/web/vite.config.ts` bettet sie als `__FA_VERSION__`/
 * `__FA_COMMIT__` zur BAUZEIT DES BÜNDELS ein — ohne Netzrunde, ohne
 * Wartezeit. `App.tsx` reicht die beiden als `version`-Zeichenkette weiter;
 * diese Komponente selbst bleibt dabei unwissend, WOHER die Zeichenkette
 * kommt — sie zeigt nur, was der Aufrufer mitgibt, und erfindet nichts, wenn
 * er es nicht tut.
 */
export function SplashScreen(props: {
  /** Der Anzeigename der angemeldeten Person; ohne ihn grüsst das Bild namenlos. */
  readonly anzeigename?: string;
  /** Die Fassungszeile, wenn der Aufrufer sie kennt. Sonst entfällt sie. */
  readonly version?: string;
  /** Eine Lagezeile unter der Begrüssung, wenn der Aufrufer eine hat. Sonst entfällt sie. */
  readonly hinweis?: string;
  /** Läuft, wenn die Zeitachse durch ist oder jemand übersprungen hat. */
  readonly onFertig: () => void;
  /** Nur für Proben: der Zeitpunkt, gegen den die Begrüssung steht. */
  readonly jetzt?: Date;
}): ReactNode {
  /**
   * Einmal beim Einhängen gelesen und danach fest.
   *
   * Wer die Einstellung mitten in einer 2,8 s langen Choreografie umstellt,
   * bekommt sie beim nächsten Anmelden — ein Umschalten mittendrin liesse eine
   * halb gelaufene Bewegung springen, und das ist genau das, was
   * `prefers-reduced-motion` verhindern soll.
   */
  const [ruhig] = useState(bewegungReduziert);

  /**
   * `onFertig` über eine Referenz und nicht als Abhängigkeit des Effekts:
   * Sonst setzte jede neue Funktionsinstanz des Aufrufers die Uhr zurück, und
   * das Bild bliebe bei einem ungünstig getakteten Rendern der Anwendung
   * darunter beliebig lange stehen.
   */
  const fertigRef = useRef(props.onFertig);
  useEffect(() => {
    fertigRef.current = props.onFertig;
  });

  useEffect(() => {
    const laufzeit = ruhig ? STANDBILD_GESAMTDAUER_MS : GESAMTDAUER_MS;
    const uhr = window.setTimeout(() => {
      fertigRef.current();
    }, laufzeit);

    const ueberspringen = (): void => {
      window.clearTimeout(uhr);
      fertigRef.current();
    };

    window.addEventListener('click', ueberspringen);
    window.addEventListener('keydown', ueberspringen);

    return () => {
      window.clearTimeout(uhr);
      window.removeEventListener('click', ueberspringen);
      window.removeEventListener('keydown', ueberspringen);
    };
  }, [ruhig]);

  const jetzt = props.jetzt ?? new Date();
  const gruss = begruessung(berlinerStunde(jetzt));
  const vorname = props.anzeigename === undefined ? undefined : ersterName(props.anzeigename);

  /**
   * Hängt eine Bewegung an ein Element — oder eben nicht.
   *
   * In der ruhigen Fassung trägt kein einzelnes Element eine Bewegung: Die
   * Keyframes enden allesamt im Ruhezustand, also steht ohne sie genau das
   * Standbild da, das der Auftrag verlangt. Bewegt wird dann nur die Blende
   * der ganzen Fläche am Schluss.
   */
  function bewegung(
    grundklassen: string,
    bewegungsklasse: string,
    takt: Takt,
    kurve: string,
  ): Bewegungsangabe {
    if (ruhig) return { className: grundklassen };
    return {
      className: `${grundklassen} ${bewegungsklasse}`,
      style: taktStil(takt, kurve),
    };
  }

  return (
    // Die ganze Fläche ist die Schaltfläche zum Überspringen; ein eigener
    // Knopf stünde im Bild und wäre für 2,8 s die einzige Bedienstelle, die
    // vom Bild ablenkt. Der Tastaturweg hängt am Fenster (siehe Effekt oben).
    <div
      className={`fixed inset-0 z-[60] overflow-hidden bg-background${
        ruhig ? ' fa-splash-blende' : ''
      }`}
      {...(ruhig
        ? {
            style: {
              animationDelay: `${String(STANDBILD_MS)}ms`,
              animationDuration: `${String(AUSBLENDEN.dauerMs)}ms`,
              animationTimingFunction: 'ease-out',
            },
          }
        : {})}
    >
      {/*
        Der einzige Text, den ein Vorleseprogramm hört. Die Wortmarke steht
        darunter Buchstabe für Buchstabe in eigenen Elementen — vorgelesen
        ergäbe das «F a m e A c c …» und keinen Namen; deshalb ist die Bühne
        vollständig `aria-hidden` und dieser eine Satz die Ansage.
      */}
      <p className="sr-only" aria-live="polite">
        FameAccounting wird gestartet
      </p>

      {/* Die Bühne hellt zur Flächenfarbe der Anwendung auf, in die sie übergibt. */}
      <div
        aria-hidden="true"
        {...bewegung('absolute inset-0 bg-card', 'fa-splash-aufhellen', TAKTE.aufhellen, KURVEN.hinein)}
      />

      {/*
        Das Punktraster wächst aus der oberen rechten Ecke herein. In der
        ruhigen Fassung entfällt es ganz: Der Auftrag nennt für sie Signet,
        Wortmarke und Begrüssung, und ein Raster ohne sein Hereinwachsen ist
        Dekoration ohne Aussage.
      */}
      {ruhig ? null : (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute right-0 top-0 fa-splash-blende"
          style={{
            width: `${String(RASTER_BREITE_PX)}px`,
            height: `${String(RASTER_HOEHE_PX)}px`,
            ...taktStil(AUSBLENDEN, 'ease-out'),
          }}
        >
          {PUNKTE.map((punkt) => (
            <div
              key={`${String(punkt.obenPx)}-${String(punkt.linksPx)}`}
              className="absolute"
              style={{
                top: `${String(punkt.obenPx)}px`,
                left: `${String(punkt.linksPx)}px`,
                width: `${String(punkt.groessePx)}px`,
                height: `${String(punkt.groessePx)}px`,
                opacity: punkt.deckung,
              }}
            >
              {/*
                Zwei Elemente je Punkt, und das ist kein Umweg: Die Ruhedeckkraft
                unterscheidet sich von Punkt zu Punkt (0,18 bis 0,68), eine
                Bewegung kann sie deshalb nicht selbst anfahren, ohne dass die
                Zahl in die Keyframes wandert. Aussen die Deckkraft, innen die
                Bewegung — genau die Multiplikation, die die Vorlage je Bild
                rechnet.
              */}
              <div
                className={`size-full rounded-pille fa-splash-punkt ${
                  punkt.nah ? 'bg-primary' : 'bg-primary/45'
                }`}
                style={taktStil(
                  { versatzMs: punkt.versatzMs, dauerMs: TAKTE.punkt.dauerMs },
                  KURVEN.aufspringen,
                )}
              />
            </div>
          ))}
        </div>
      )}

      {/* Die Bühne: Signet, Wortmarke, Begrüssung. */}
      <div
        aria-hidden="true"
        {...bewegung(
          'absolute inset-0 flex flex-col items-center justify-center gap-[30px] px-6',
          'fa-splash-weg',
          AUSBLENDEN,
          'ease-out',
        )}
      >
        <div className="relative grid size-[168px] place-items-center">
          {/*
            Der Halo: dieselbe Markenfarbe, weichgezeichnet — die Vorlage malt
            dafür einen Radialverlauf mit eigenem Farbwert. `blur-3xl` auf der
            Rollenfarbe kommt ohne einen zweiten Farbwert aus und schaltet mit
            der Tokenschicht zwischen hell und dunkel mit.
          */}
          <div
            {...bewegung(
              'absolute size-[240px] rounded-pille bg-primary/25 blur-3xl',
              'fa-splash-halo',
              TAKTE.halo,
              KURVEN.hinein,
            )}
          />
          <svg
            viewBox="0 0 64 64"
            {...bewegung(
              'relative size-[108px] overflow-visible',
              'fa-splash-aufrichten',
              TAKTE.aufrichten,
              KURVEN.hinein,
            )}
          >
            {/*
              Dieselbe Geometrie wie `FameSignet` (`FAME_SIGNET_BALKEN`, `E-1906`),
              nur mit einer Bewegung je Balken: Er wächst von seiner linken Kante
              aus, der nächste setzt `BALKEN.versatzMs` später ein. Die ruhige
              Fassung zeigt die Balken ohne Klasse und damit in voller Länge.
            */}
            {FAME_SIGNET_BALKEN.map((balken, i) => (
              <rect
                key={balken.y}
                x={balken.x}
                y={balken.y}
                width={balken.breite}
                height={balken.hoehe}
                rx={balken.radius}
                {...bewegung(balken.fuellung, 'fa-splash-balken', balkenTakt(i), KURVEN.hinein)}
              />
            ))}
          </svg>
        </div>

        <div className="flex flex-col items-center gap-[15px]">
          <div className="flex items-baseline overflow-hidden py-0.5">
            {[...WORTMARKE].map((zeichen, i) => (
              <span
                // Der Index gehört in den Schlüssel: «FameAccounting» trägt
                // dasselbe Zeichen mehrfach, und die Zeichenfolge ist eine
                // Konstante — es wird nie umsortiert.
                key={`${zeichen}-${String(i)}`}
                {...bewegung(
                  `inline-block whitespace-pre text-[44px] tracking-[-0.035em] text-foreground ${
                    i < WORTMARKE_TRENNUNG ? 'font-bold' : 'font-normal'
                  }`,
                  'fa-splash-buchstabe',
                  {
                    versatzMs: TAKTE.buchstabe.versatzMs + i * BUCHSTABEN_VERSATZ_MS,
                    dauerMs: TAKTE.buchstabe.dauerMs,
                  },
                  KURVEN.hinein,
                )}
              >
                {zeichen}
              </span>
            ))}
          </div>

          <div className="h-0.5 w-[320px] overflow-hidden rounded-sm bg-track">
            <div
              {...bewegung(
                'h-full w-full rounded-sm bg-primary',
                'fa-splash-linie',
                TAKTE.linie,
                KURVEN.hinein,
              )}
            />
          </div>

          {props.version === undefined ? null : (
            <div
              {...bewegung(
                'zahl text-[11.5px] tracking-[0.06em] text-text3',
                'fa-splash-fassung',
                TAKTE.fassung,
                KURVEN.hinein,
              )}
            >
              {props.version}
            </div>
          )}
        </div>

        <div
          {...bewegung(
            'mt-1 text-center',
            'fa-splash-begruessung',
            TAKTE.begruessung,
            KURVEN.hinein,
          )}
        >
          <div className="mb-[5px] text-lg font-semibold tracking-[-0.02em] text-foreground">
            {vorname === undefined ? gruss : `${gruss}, ${vorname}`}
          </div>
          {props.hinweis === undefined ? null : (
            <div className="text-[13px] text-text3">{props.hinweis}</div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Die Anrede: der erste Namensteil, nicht der ganze Anzeigename. */
export function ersterName(anzeigename: string): string {
  const teile = anzeigename.trim().split(/\s+/);
  const erster = teile[0];
  return erster === undefined || erster === '' ? anzeigename : erster;
}

/**
 * Ob das Betriebssystem Bewegung abbestellt hat.
 *
 * In `try`, weil `matchMedia` in einer Umgebung ohne Fensterabfragen fehlen
 * kann — dann läuft die Choreografie, und das ist die richtige Voreinstellung:
 * Wer nichts bestellt hat, hat auch nichts abbestellt.
 */
function bewegungReduziert(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}
